<template>
  <form @submit.prevent="handleSubmit" class="space-y-6">
    <div class="text-center mb-6">
      <h2 class="text-2xl font-bold text-gray-900 mb-2">Set new password</h2>
      <p class="text-gray-600">Please enter your new password below.</p>
    </div>

    <div class="space-y-4">
      <Input
        v-model="formData.password"
        type="password"
        label="New Password"
        placeholder="Enter your new password"
        required
        :error="errors.password"
        :disabled="isLoading || resetComplete"
        :hint="passwordHint"
        @blur="handlePasswordBlur"
      />

      <Input
        v-model="formData.confirmPassword"
        type="password"
        label="Confirm New Password"
        placeholder="Confirm your new password"
        required
        :error="errors.confirmPassword"
        :disabled="isLoading || resetComplete"
        @blur="handleConfirmPasswordBlur"
      />
    </div>

    <!-- Password requirements -->
    <div
      v-if="formData.password && !passwordIsValid"
      class="bg-blue-50 border border-blue-200 rounded-md p-4"
    >
      <h4 class="text-sm font-medium text-blue-800 mb-2">Password requirements:</h4>
      <ul class="text-sm text-blue-700 space-y-1">
        <li class="flex items-center">
          <svg
            :class="formData.password.length >= 8 ? 'text-green-500' : 'text-gray-400'"
            class="h-4 w-4 mr-2"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fill-rule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clip-rule="evenodd"
            />
          </svg>
          At least 8 characters long
        </li>
        <li class="flex items-center">
          <svg
            :class="/(?=.*[a-z])/.test(formData.password) ? 'text-green-500' : 'text-gray-400'"
            class="h-4 w-4 mr-2"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fill-rule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clip-rule="evenodd"
            />
          </svg>
          At least one lowercase letter
        </li>
        <li class="flex items-center">
          <svg
            :class="/(?=.*[A-Z])/.test(formData.password) ? 'text-green-500' : 'text-gray-400'"
            class="h-4 w-4 mr-2"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fill-rule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clip-rule="evenodd"
            />
          </svg>
          At least one uppercase letter
        </li>
        <li class="flex items-center">
          <svg
            :class="/(?=.*\d)/.test(formData.password) ? 'text-green-500' : 'text-gray-400'"
            class="h-4 w-4 mr-2"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fill-rule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clip-rule="evenodd"
            />
          </svg>
          At least one number
        </li>
      </ul>
    </div>

    <div v-if="resetComplete" class="rounded-md bg-green-50 p-4">
      <div class="flex">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clip-rule="evenodd"
            />
          </svg>
        </div>
        <div class="ml-3">
          <h3 class="text-sm font-medium text-green-800">Password reset successful!</h3>
          <div class="mt-2 text-sm text-green-700">
            <p>
              Your password has been successfully reset. You can now sign in with your new password.
            </p>
          </div>
        </div>
      </div>
    </div>

    <div>
      <Button
        v-if="!resetComplete"
        type="submit"
        variant="primary"
        size="lg"
        :loading="isLoading"
        :disabled="!isFormValid"
        full-width
      >
        Reset password
      </Button>

      <Button v-else variant="primary" size="lg" full-width @click="goToLogin">
        Go to Sign In
      </Button>
    </div>

    <div v-if="!resetComplete" class="text-center">
      <p class="text-sm text-gray-600">
        Remember your password?
        <router-link
          to="/auth/login"
          class="font-medium text-blue-600 hover:text-blue-500 transition-colors"
        >
          Sign in
        </router-link>
      </p>
    </div>
  </form>
</template>

<script setup lang="ts">
  import { ref, computed, reactive, onMounted } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import type { AxiosError } from 'axios';
  import { Button, Input } from '@asabdev/asab-ui';
  import { useAuth } from '../composables/useAuth';
  import { AUTH_ERROR_CODES, isAuthError } from '@/lib/api/errors';

  const emit = defineEmits<{
    success: [];
    error: [error: string];
    // Backend rejected reset because account is banned/deactivated. Page
    // shows a per-status banner instead of generic error toast.
    'account-disabled': [
      info: {
        status: 'pending_approval' | 'banned' | 'suspended' | 'deactivated' | 'disabled';
        message: string;
        waitlist_url: string;
        support_email: string;
      },
    ];
  }>();

  const route = useRoute();
  const router = useRouter();
  const { resetPassword, isLoading, error: authError, clearError } = useAuth();

  // Form state
  const resetComplete = ref(false);
  const resetToken = ref<string>('');

  // Form data
  const formData = reactive({
    password: '',
    confirmPassword: '',
  });

  // Form validation errors
  const errors = reactive({
    password: '',
    confirmPassword: '',
  });

  // Validation rules
  const validatePassword = (password: string): string => {
    if (!password) return 'Password is required';
    if (password.length < 8) return 'Password must be at least 8 characters long';
    if (!/(?=.*[a-z])/.test(password)) return 'Password must contain at least one lowercase letter';
    if (!/(?=.*[A-Z])/.test(password)) return 'Password must contain at least one uppercase letter';
    if (!/(?=.*\d)/.test(password)) return 'Password must contain at least one number';
    return '';
  };

  const validateConfirmPassword = (password: string, confirmPassword: string): string => {
    if (!confirmPassword) return 'Please confirm your password';
    if (password !== confirmPassword) return 'Passwords do not match';
    return '';
  };

  // Computed properties
  const passwordIsValid = computed(() => {
    return validatePassword(formData.password) === '';
  });

  const passwordHint = computed(() => {
    if (!formData.password) {
      return 'Password must meet the security requirements below';
    }
    return '';
  });

  const isFormValid = computed(() => {
    return (
      formData.password &&
      formData.confirmPassword &&
      !errors.password &&
      !errors.confirmPassword &&
      resetToken.value
    );
  });

  // Methods
  const validateForm = (): boolean => {
    errors.password = validatePassword(formData.password);
    errors.confirmPassword = validateConfirmPassword(formData.password, formData.confirmPassword);

    return !errors.password && !errors.confirmPassword;
  };

  const handleSubmit = async () => {
    // Clear previous auth errors
    clearError();

    // Validate form
    if (!validateForm()) {
      return;
    }

    if (!resetToken.value) {
      emit('error', 'Invalid or missing reset token');
      return;
    }

    try {
      await resetPassword(resetToken.value, formData.password);

      resetComplete.value = true;
      emit('success');
    } catch (error) {
      // Map per-status disabled responses (banned / deactivated reject the
      // reset). Pending and suspended pass the BE allow-list.
      const axErr = error as AxiosError<{
        error?: string;
        waitlist_url?: string;
        support_email?: string;
      }>;
      const disabledStatus = isAuthError(axErr, AUTH_ERROR_CODES.LOGIN_ACCOUNT_BANNED)
        ? 'banned'
        : isAuthError(axErr, AUTH_ERROR_CODES.LOGIN_ACCOUNT_SUSPENDED)
          ? 'suspended'
          : isAuthError(axErr, AUTH_ERROR_CODES.LOGIN_ACCOUNT_DEACTIVATED)
            ? 'deactivated'
            : isAuthError(axErr, AUTH_ERROR_CODES.LOGIN_ACCOUNT_DISABLED)
              ? 'disabled'
              : isAuthError(axErr, AUTH_ERROR_CODES.LOGIN_PENDING_APPROVAL)
                ? 'pending_approval'
                : null;
      if (disabledStatus) {
        const data = axErr.response?.data ?? {};
        emit('account-disabled', {
          status: disabledStatus,
          message: data.error ?? 'Password reset is not available for this account.',
          waitlist_url: data.waitlist_url ?? '',
          support_email: data.support_email ?? '',
        });
        return;
      }
      const errorMessage = authError.value || 'Failed to reset password. Please try again.';
      emit('error', errorMessage);
    }
  };

  const goToLogin = () => {
    router.push('/auth/login');
  };

  // Real-time validation handlers
  const handlePasswordBlur = () => {
    if (formData.password) {
      errors.password = validatePassword(formData.password);
    }
  };

  const handleConfirmPasswordBlur = () => {
    if (formData.confirmPassword) {
      errors.confirmPassword = validateConfirmPassword(formData.password, formData.confirmPassword);
    }
  };

  // Initialize component
  onMounted(() => {
    // Get reset token from URL query parameter
    const token = route.query.token as string;
    if (token) {
      resetToken.value = token;
    } else {
      emit('error', 'Invalid or missing reset token');
    }
  });
</script>
